import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import routes from "@/routes/routes";
import { StatCardsRow } from "@/components/StatCard/StatCard";
import type { StatCardData } from "@/components/StatCard/StatCard";
import Button from "@/components/Button/Button";
import UsersIcon from "@/components/SVGComponents/UsersIcon";
import { usePageSubtitle } from "@/hooks/usePageSubtitle";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getDashboardSummary } from "@/store/slices/dashboardSlice";
import { formatINRCompact } from "@/lib/currency";
import UserPlusIcon from "./components/icons/UserPlusIcon";
import CardIcon from "./components/icons/CardIcon";
import CalendarIcon from "@/components/SVGComponents/CalendarIcon";
import MembershipGrowthChart from "./components/MembershipGrowthChart/MembershipGrowthChart";
import ApplicationFunnel from "./components/ApplicationFunnel/ApplicationFunnel";
import UpcomingGatherings from "./components/UpcomingGatherings/UpcomingGatherings";
import styles from "./Dashboard.module.scss";


const CURRENT_YEAR = new Date().getFullYear();
const DEFAULT_GROWTH_PERIOD_MONTHS = 12;

const getSubtitle = () => {
  const today = new Date();
  const weekday = today.toLocaleDateString("en-IN", { weekday: "long" });
  const date = today.toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });
  // "All cities" is a scope placeholder — no city filter exists on this page yet.
  return `${weekday} · ${date} · All cities`;
};

const Dashboard = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { summary, isFetching, error, totalUpcomingEvents, isFetchingUpcomingEvents } = useAppSelector(
    (state) => state.dashboard,
  );
  const [growthPeriod, setGrowthPeriod] = useState(DEFAULT_GROWTH_PERIOD_MONTHS);
  usePageSubtitle(getSubtitle());

  const fetchSummary = useCallback(() => {
    dispatch(getDashboardSummary({ revenueYear: CURRENT_YEAR, growthPeriod }));
  }, [dispatch, growthPeriod]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const hasFailedWithNoData = Boolean(error) && !summary;
  const isInitialLoad = isFetching && !summary;

  const dashboardStats: StatCardData[] = [
    {
      label: "Active Members",
      value: summary ? summary.activeMemberCount.toLocaleString("en-IN") : "—",
      icon: <UsersIcon />,
      onClick: () => navigate(routes.privateRoutes.MEMBERS.path),
    },
    {
      label: "Pending Applications",
      value: summary ? summary.pendingApplicationCount.toLocaleString("en-IN") : "—",
      icon: <UserPlusIcon />,
      onClick: () => navigate(routes.privateRoutes.WAITLIST.path),
    },
    {
      label: "Annual Recurring Revenue",
      value: summary ? formatINRCompact(summary.annualRecurringRevenue.amount) : "—",
      icon: <CardIcon />,
      onClick: () => navigate(routes.privateRoutes.PAYMENTS.path),
    },
    {
      label: "Total Gatherings",
      value:
        isFetchingUpcomingEvents && totalUpcomingEvents === 0
          ? "—"
          : totalUpcomingEvents.toLocaleString("en-IN"),
      icon: <CalendarIcon />,
      onClick: () => navigate(routes.privateRoutes.GATHERINGS.path),
    },
  ];

  return (
    <div className={styles.dashboard}>
      {hasFailedWithNoData ? (
        <div className={styles.errorState}>
          <p className={styles.errorTitle}>Couldn&apos;t load the dashboard</p>
          <p className={styles.errorCaption}>{error}</p>
          <Button type="button" onClick={fetchSummary}>
            Try again
          </Button>
        </div>
      ) : (
        <>
          <StatCardsRow stats={dashboardStats} />

          <div className={styles.chartsRow}>
            <MembershipGrowthChart
              data={summary?.membershipGrowth.data ?? []}
              isLoading={isInitialLoad}
              period={growthPeriod}
              onPeriodChange={setGrowthPeriod}
            />
            <ApplicationFunnel data={summary?.applicationFunnel ?? null} isLoading={isInitialLoad} />
          </div>
        </>
      )}

      <UpcomingGatherings />
    </div>
  );
};

export default Dashboard;
